import type {
  BaseSelection,
  BaseSelectionItem,
} from "@frend-digital/centra-types/selection";
import type { ProductDetails } from "../centra/formatters";
import { getBaseUrl } from "../utils/getBaseUrl";
import { selectionItemToKlaviyoItem } from "./utils";

export const klaviyoTracker = {
  addToCart: (selection: BaseSelection, item?: BaseSelectionItem) => {
    if (
      !window.klaviyo ||
      !selection ||
      !selection.items ||
      selection.items.length === 0
    )
      return;

    const addedItem = (item ||
      selection.items.at(selection.items.length - 1)) as
      | BaseSelectionItem
      | undefined;

    if (!addedItem) return;

    const baseUrl = getBaseUrl();

    const ecommerce = {
      $value: selection?.totals?.grandTotalPriceAsNumber,
      AddedItemProductName: addedItem.product?.name,
      AddedItemProductID: addedItem.product?.productSku,
      AddedItemSKU: addedItem.product?.productSku,
      AddedItemCategories: addedItem.product?.categoryName,
      AddedItemImageURL: addedItem.product?.media?.["standard"]?.[0],
      AddedItemURL: `${baseUrl}/${addedItem.product?.uri}`,
      AddedItemPrice: addedItem.priceEach,
      AddedItemQuantity: 1,
      ItemNames: selection.items.map(item => item.product?.name),
      CheckoutURL: `${baseUrl}/checkout`,
      Items: selection?.items?.map(selectionItemToKlaviyoItem),
    };

    window.klaviyo.track("Added to Cart", ecommerce);
  },
  removeFromCart: (
    selection: BaseSelection,
    isRemoveAllFromCart: boolean,
    item?: BaseSelectionItem,
  ) => {
    if (
      !window.klaviyo ||
      !selection ||
      !selection.items ||
      selection.items.length === 0
    )
      return;

    const removedItem = item;

    if (!removedItem) return;

    const baseUrl = getBaseUrl();

    const ecommerce = {
      $value: selection?.totals?.grandTotalPriceAsNumber,
      RemovedItemProductName: removedItem.product?.name,
      RemovedItemProductID: removedItem.product?.productSku,
      RemovedItemSKU: removedItem.product?.productSku,
      RemovedItemCategories: removedItem.product?.categoryName,
      RemovedItemImageURL: removedItem.product?.media?.["standard"]?.[0],
      RemovedItemURL: `${baseUrl}/${removedItem.product?.uri}`,
      RemovedItemPrice: removedItem.priceEach,
      RemovedItemQuantity: isRemoveAllFromCart ? removedItem.quantity : 1,
      ItemNames: selection.items.map(item => item.product?.name),
      CheckoutURL: `${baseUrl}/checkout`,
      Items: selection?.items?.map(selectionItemToKlaviyoItem),
    };

    window.klaviyo.track("Removed from Cart", ecommerce);
  },
  viewProduct: (product: ProductDetails) => {
    if (!window.klaviyo) return;

    const baseUrl = getBaseUrl();

    const ecommerce = {
      ProductName: product.name,
      ProductID: product.uri,
      SKU: product.sku,
      Categories: product.canonicalUri,
      ImageURL: product.media.at(0)?.href,
      URL: `${baseUrl}/${product.uri}`,
      Brand: "Blæst",
      Price: product?.priceAsNumber ?? 0,
      CompareAtPrice: product?.priceAsNumber ?? 0,
    };

    window.klaviyo.track("Viewed Product", ecommerce);
  },
  startCheckout: (selection: BaseSelection, token?: string) => {
    if (
      !window.klaviyo ||
      !selection ||
      !selection.items ||
      selection.items.length === 0
    )
      return;

    const baseUrl = getBaseUrl();

    const ecommerce = {
      $event_id: token,
      $value: selection?.totals?.grandTotalPriceAsNumber,
      ItemNames: [selection.items.map(item => item.product?.name).join(", ")],
      CheckoutURL: `${baseUrl}/checkout`,
      Categories: new Set(
        selection.items.flatMap(item => item.product?.categoryName),
      ),
      Items: selection?.items?.map(selectionItemToKlaviyoItem),
    };

    window.klaviyo.track("Started Checkout (Custom)", ecommerce);
  },
  identify: (email: string) => {
    if (!window.klaviyo) return;

    window.klaviyo.identify({
      email,
    });
  },
};
